/**
 * BUG-139.3-003 — recovery from a model id the server no longer knows.
 *
 * The model catalogue and the stored user preference are each read once per app
 * session (`agentModelsStore`), while a chat tab can stay open for hours. If the
 * preferred model leaves the effective catalogue in that window — a rotated
 * credential, an Agent deploy — the client keeps stamping the cached id onto every
 * outgoing request as an explicit `modelId`. That is the resolver's *hard* layer:
 * `ModelResolver` step 1 has no tolerance for an unknown override, so the request
 * comes back 400 and keeps doing so for the life of the tab, because neither cached
 * read is ever re-taken. Only a page reload recovered.
 *
 * This module recognises that one rejection and re-reads both, so every picker offers
 * what actually exists. Nothing has to be cleared by hand: `fetchModels` reconciles a
 * stale session pin (BUG-139.3-002), and `GET /api/agent/preferences` already answers
 * `null` for a stored id the server can no longer resolve.
 *
 * The request is deliberately **not** re-sent. An unknown request-level model id means
 * the caller's choice cannot be honoured, and quietly answering on a substitute model
 * is a worse outcome than a visible failure.
 *
 * ## Placement
 *
 * All the agent-model knowledge — which rejection this is, what to re-read, what to
 * tell the user — lives here, beside the store that owns the caches. The transport
 * knows none of it: it only announces that some rejection is reaching its caller
 * (`onHttpRejection`), and this module decides whether it is interesting. That keeps
 * `axiosInstance` a transport and still covers **every** door, because the announcement
 * travels on `window` — one object per tab — rather than through one axios instance.
 * Chat sends and the shell's Profile preference `PUT` are both covered even though
 * `api/axiosInstance` is not a webpack share key and need not be a single instance.
 */

// Relative is correct here, and deliberately so. The usual rule — reach across
// shared-domain by package specifier, never relatively — exists to avoid duplicate
// *stateful* singletons (a second Zustand store). This helper holds no state: it
// dispatches on and listens to `window`, and matches a string constant, so two copies of
// it are indistinguishable in behaviour. Nothing here needs to be a singleton, which is
// the same property that lets this recovery span every copy of the transport.
import { onHttpRejection } from '../api/httpRejectionEvent';

/**
 * Machine-readable discriminator on the Agent service's 400 body
 * (`UnknownModelExceptionFilter.UnknownModelCode`). Preferred over the prose field.
 */
export const UNKNOWN_MODEL_CODE = 'unknown_model';

/**
 * Legacy discriminator: the `error` field, which was the only handle before `code`
 * was added. Still accepted so a frontend deployed ahead of the Agent service keeps
 * recovering rather than silently regressing.
 */
export const UNKNOWN_MODEL_ERROR_LABEL = 'Unknown model';

/**
 * Replaces Axios's `"Request failed with status code 400"` for this one rejection.
 *
 * The chat adapter's user-facing send-error mapper surfaces `error.message` verbatim,
 * and the Profile section shows the store's `saveError`, which is taken from the same
 * message — so rewriting it in place (rather than throwing a new error) carries this
 * text to both with no change to either. Worded without assuming a send: the same
 * rejection arrives from the preference `PUT`, where "try again" means saving.
 */
export const UNKNOWN_MODEL_USER_MESSAGE =
  'The selected AI model is no longer available. The model list has been refreshed — ' +
  'pick another model and try again.';

/** True for the Agent service's unknown-model 400, by `code` or the legacy `error`. */
export function isUnknownModelRejection(error: unknown): boolean {
  const response = (error as { response?: { status?: number; data?: unknown } } | null | undefined)
    ?.response;
  if (response?.status !== 400) {
    return false;
  }
  const data = response.data as { code?: unknown; error?: unknown } | null | undefined;
  return data?.code === UNKNOWN_MODEL_CODE || data?.error === UNKNOWN_MODEL_ERROR_LABEL;
}

let unsubscribe: (() => void) | null = null;

/**
 * Start listening. Idempotent — a second call is a no-op rather than a second listener,
 * so module re-evaluation under Module Federation cannot double up.
 *
 * `refresh` is supplied by the store rather than imported here, which keeps the
 * dependency one-way (store → recovery) and avoids an import cycle. It is started but
 * **not awaited**: the request has already failed, and blocking its error on further
 * network reads would leave the composer spinning for the length of the axios timeout
 * when the backend is the thing that is unwell. Its rejection is swallowed so a failed
 * refresh cannot surface as a second, unrelated error on top of the first.
 */
export function subscribeUnknownModelRecovery(refresh: () => Promise<void>): void {
  if (unsubscribe !== null) {
    return;
  }
  unsubscribe = onHttpRejection((error) => {
    if (!isUnknownModelRejection(error)) {
      return;
    }
    void refresh().catch(() => undefined);
    if (error instanceof Error) {
      error.message = UNKNOWN_MODEL_USER_MESSAGE;
    }
  });
}

/** Test seam: stop listening so a suite can subscribe again from scratch. */
export function unsubscribeUnknownModelRecovery(): void {
  unsubscribe?.();
  unsubscribe = null;
}
